import { Trans } from '@lingui/macro'
import { ChangeCircle, LowPriority } from '@mui/icons-material'
import { Button, Dialog, DialogContent, DialogContentText, Grid, Icon } from '@mui/material'
import { setOrganization } from 'app/redux/actions/OrganizationActions'
import { ReduxOrganization, ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import { getAccountParsedById } from 'app/services/sfAuth/sfData/sfAccount'
import { saveUser } from 'app/services/sfAuth/sfData/sfUser'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import ProgressSnackbar from '../common-components/CustomSnackbars'
import { DialogTitleWithIconClose } from '../common-components/DialogTitleWithIconClose'
import { FUNDING_PATHS } from '../funding/FundingConstants'
import { useFormContext } from './form-page/FormContext'
import { ConnectedMap } from './FormTypes'

export const FormOrganizationMissmatchPopUp: React.FC<{
  previousOrganization?: string | undefined
  handleSwitch: () => void
  connectedMap: ConnectedMap
}> = ({ previousOrganization, connectedMap, handleSwitch }) => {
  const [updating, setUpdating] = useState(false)
  const { mainConnectedObject } = useFormContext()
  const user = useSelector<ReduxState, ReduxUser>(state => state.user)
  const organization = useSelector<ReduxState, ReduxOrganization>((state) => state.organization)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const history = useHistory()

  const handleOrganizationChange = (): void => {
    const obj = Object.values(connectedMap).find(obj => obj.objectType === mainConnectedObject)?.sfObject
    if (obj == null) return
    if (mainConnectedObject === 'Opportunity') {
      if (obj.Account.Id != null && organization.id !== obj.Account.Id) {
        history.push(FUNDING_PATHS.APPLICATIONS_LIST)
      }
    } else if (mainConnectedObject === 'Pre_Qualification__c') {
      if (obj.Organization_Legal_Name__c != null && organization.id !== obj.Organization_Legal_Name__c) {
        history.push(FUNDING_PATHS.APPLICATIONS_LIST)
      }
    } else if (mainConnectedObject === 'FGM_Base__Grantee_Report__c') {
      if (obj.FGM_Base__Request__r?.AccountId != null && organization.id !== obj.FGM_Base__Request__r?.AccountId) {
        history.push(FUNDING_PATHS.REPORTS)
      }
    }
  }

  if (previousOrganization == null) return null

  return (
    <Dialog maxWidth='xs' fullWidth open={previousOrganization != null}>
      <DialogTitleWithIconClose
        label={<Trans>ORGANIZATION_MISSMATCH_TITLE</Trans>}
        handleClose={handleOrganizationChange}
        disabled={updating}
      />
      <DialogContent sx={{ paddingLeft: '48px', paddingRight: '48px' }}>
        <DialogContentText>
          <Trans>ORGANIZATION_MISSMATCH_TEXT</Trans>
        </DialogContentText>
        <Grid sx={{ mt: '32px' }} container direction='column' gap='16px'>
          <Button
            style={{ marginTop: 10 }}
            color='primary'
            variant='contained'
            disabled={updating}
            onClick={() => {
              setUpdating(true)
              const saving = enqueueSnackbar('', {
                variant: 'info',
                persist: true,
                content: (key) =>
                  ProgressSnackbar(<Trans>Selecting organization</Trans>)
              })
              Promise.all([
                getAccountParsedById(previousOrganization, {
                  permissions: { TEAM_MEMBERS: true }
                }),
                saveUser({
                  Id: user.userId,
                  Associated_Organizations__c: previousOrganization
                })
              ]).then(
                ([org, saveUser]) => {
                  const newCurrent = { ...org }
                  closeSnackbar(saving)
                  enqueueSnackbar(<Trans>Organization selected!</Trans>, {
                    variant: 'success'
                  })
                  dispatch(setOrganization(newCurrent))
                  setUpdating(false)
                  handleSwitch()
                },
                (reject) => {
                  closeSnackbar(saving)
                  enqueueSnackbar(
                    <Trans>Error occured while selecting this organization!</Trans>,
                    {
                      variant: 'error'
                    }
                  )
                  setUpdating(false)
                }
              )
            }}
          >
            <Icon sx={{ marginRight: '8px' }}>
              <ChangeCircle />
            </Icon>
            <Trans>SWITCH</Trans>
          </Button>
          <Button
            disabled={updating}
            color='primary'
            variant='outlined'
            onClick={handleOrganizationChange}
          >
            <Icon sx={{ marginRight: '8px' }}>
              <LowPriority />
            </Icon>
            <Trans>REDIRECT</Trans>
          </Button>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
